import React, { useState, useEffect } from 'react';
import { X, CheckCircle, Undo2, Plus } from 'lucide-react';
import api from '../../services/api';
import DataTable from '../Tables/DataTable';
import ProjectForm from '../Forms/ProjectForm';
import ConfirmationModal from './ConfirmationModal';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency } from '../../utils/currency';

interface CustomerProjectsModalProps {
    isOpen: boolean;
    onClose: () => void;
    customerId: string;
    customerName: string;
}

const CustomerProjectsModal: React.FC<CustomerProjectsModalProps> = ({ isOpen, onClose, customerId, customerName }) => {
    const { currency } = useAuth();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingProject, setEditingProject] = useState<any>(null);
    const [creatingProject, setCreatingProject] = useState(false);

    // Confirmation Modal State
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [confirmProject, setConfirmProject] = useState<any>(null);
    const [confirmAmount, setConfirmAmount] = useState<number>(0);

    // Undo Modal State
    const [undoModalOpen, setUndoModalOpen] = useState(false);
    const [undoProject, setUndoProject] = useState<any>(null);

    useEffect(() => {
        const fetchProjects = async () => {
            if (!isOpen || !customerId) return;
            setLoading(true);
            try {
                const res = await api.get(`/projects?customerId=${customerId}`);
                setProjects(res.data);
            } catch (err) {
                console.error('Failed to fetch customer projects', err);
            } finally {
                setLoading(false);
            }
        };

        fetchProjects();
    }, [isOpen, customerId]);

    const handleProjectSuccess = async () => {
        setEditingProject(null);
        setLoading(true);
        try {
            const res = await api.get(`/projects?customerId=${customerId}`);
            setProjects(res.data);
        } catch (err) {
            console.error('Failed to fetch customer projects', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCompleteProject = async (project: any) => {
        const rate = project.hourlyRate || 0;
        let total = 0;

        if (project.billingMethod === 'Fixed Cost') {
            total = rate;
        } else if (rate && project.timesheets && project.timesheets.length > 0) {
            const totalHours = project.timesheets.reduce((acc: number, t: any) => {
                const [h, m] = t.timeSpent.split(':').map(Number);
                return acc + (h + m / 60);
            }, 0);
            total = totalHours * rate;
        }

        let paid = 0;
        if (project.installments && project.installments.length > 0) {
            paid = project.installments.reduce((acc: number, inst: any) => {
                return inst.status === 'Paid' ? acc + inst.amount : acc;
            }, 0);
        } else {
            paid = project.receivedAmount || 0;
        }

        const remaining = total - paid;

        if (remaining <= 0) return;

        setConfirmProject(project);
        setConfirmAmount(remaining);
        setConfirmModalOpen(true);
    };

    const onConfirmProject = async () => {
        if (!confirmProject) return;

        const project = confirmProject;
        const remaining = confirmAmount;

        const rate = project.hourlyRate || 0;
        let total = 0;

        if (project.billingMethod === 'Fixed Cost') {
            total = rate;
        } else if (rate && project.timesheets && project.timesheets.length > 0) {
            const totalHours = project.timesheets.reduce((acc: number, t: any) => {
                const [h, m] = t.timeSpent.split(':').map(Number);
                return acc + (h + m / 60);
            }, 0);
            total = totalHours * rate;
        }

        try {
            // First create a payment for the remaining amount
            await api.post('/payments', {
                customerId: project.customerId,
                amount: remaining,
                paymentDate: new Date().toISOString().split('T')[0],
                paymentMode: 'Bank',
                depositTo: 'Bank Account',
                projectId: project.id,
                reference: `Automatic Payment for Project: ${project.projectName}`
            });

            // Then update the project's received amount
            await api.put(`/projects/${project.id}`, {
                ...project,
                receivedAmount: total,
                installments: project.installments || [],
                tasks: project.tasks || [],
                developers: project.developers || []
            });

            // Refresh projects
            setConfirmModalOpen(false);
            setConfirmProject(null);
            handleProjectSuccess();
        } catch (error) {
            console.error('Failed to complete project', error);
            alert('Failed to complete project. Please try again.');
        }
    };

    const handleUndoComplete = (project: any) => {
        setUndoProject(project);
        setUndoModalOpen(true);
    };

    const onUndoComplete = async () => {
        if (!undoProject) return;
        const project = undoProject;

        try {
            // Find the automatic payment
            const paymentsRes = await api.get('/payments');
            const targetReference = `Automatic Payment for Project: ${project.projectName}`;
            const paymentToDelete = paymentsRes.data.find(
                (p: any) => p.reference === targetReference && p.amount === project.receivedAmount
            );

            if (paymentToDelete) {
                await api.delete(`/payments/${paymentToDelete.id}`);
            }

            // Revert project receivedAmount
            await api.put(`/projects/${project.id}`, {
                ...project,
                receivedAmount: 0,
                installments: project.installments || [],
                tasks: project.tasks || [],
                developers: project.developers || []
            });

            setUndoModalOpen(false);
            setUndoProject(null);
            handleProjectSuccess();
        } catch (error) {
            console.error('Failed to undo project completion', error);
            alert('Failed to undo project completion. Please try again.');
        }
    };

    if (!isOpen) return null;

    const columns = [
        { header: 'Project Name', accessor: 'projectName' },
        { header: 'Billing Method', accessor: 'billingMethod' },
        {
            header: 'Total Amount',
            accessor: (item: any) => {
                const rate = item.hourlyRate || 0;
                let subtotal = 0;

                if (item.billingMethod === 'Fixed Cost') {
                    subtotal = rate;
                } else if (rate && item.timesheets && item.timesheets.length > 0) {
                    const totalHours = item.timesheets.reduce((acc: number, t: any) => {
                        const [h, m] = t.timeSpent.split(':').map(Number);
                        return acc + (h + m / 60);
                    }, 0);
                    subtotal = totalHours * rate;
                }

                return formatCurrency(subtotal, currency);
            }
        },
        {
            header: 'Paid Amount',
            accessor: (item: any) => {
                let paid = 0;
                if (item.installments && item.installments.length > 0) {
                    paid = item.installments.reduce((acc: number, inst: any) => {
                        return inst.status === 'Paid' ? acc + inst.amount : acc;
                    }, 0);
                } else {
                    paid = item.receivedAmount || 0;
                }
                return formatCurrency(paid, currency);
            }
        },
        {
            header: 'Remaining Amount',
            accessor: (item: any) => {
                const rate = item.hourlyRate || 0;
                let total = 0;

                if (item.billingMethod === 'Fixed Cost') {
                    total = rate;
                } else if (rate && item.timesheets && item.timesheets.length > 0) {
                    const totalHours = item.timesheets.reduce((acc: number, t: any) => {
                        const [h, m] = t.timeSpent.split(':').map(Number);
                        return acc + (h + m / 60);
                    }, 0);
                    total = totalHours * rate;
                }

                let paid = 0;
                if (item.installments && item.installments.length > 0) {
                    paid = item.installments.reduce((acc: number, inst: any) => {
                        return inst.status === 'Paid' ? acc + inst.amount : acc;
                    }, 0);
                } else {
                    paid = item.receivedAmount || 0;
                }

                const remaining = total - paid;
                return formatCurrency(remaining, currency);
            }
        }
    ];

    let totalAllProjects = 0;
    let totalPaidAll = 0;
    let totalRemainingAll = 0;

    projects.forEach((item: any) => {
        const rate = item.hourlyRate || 0;
        let subtotal = 0;

        if (item.billingMethod === 'Fixed Cost') {
            subtotal = rate;
        } else if (rate && item.timesheets && item.timesheets.length > 0) {
            const totalHours = item.timesheets.reduce((acc: number, t: any) => {
                const [h, m] = t.timeSpent.split(':').map(Number);
                return acc + (h + m / 60);
            }, 0);
            subtotal = totalHours * rate;
        }

        let paid = 0;
        if (item.installments && item.installments.length > 0) {
            paid = item.installments.reduce((acc: number, inst: any) => {
                return inst.status === 'Paid' ? acc + inst.amount : acc;
            }, 0);
        } else {
            paid = item.receivedAmount || 0;
        }

        const remaining = subtotal - paid;

        totalAllProjects += subtotal;
        totalPaidAll += paid;
        totalRemainingAll += remaining;
    });

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] flex flex-col">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-gray-50 rounded-t-lg">
                    <h2 className="text-xl font-semibold text-gray-800">
                        Projects for {customerName}
                    </h2>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setCreatingProject(true)}
                            className="flex items-center gap-1.5 bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 transition text-sm font-medium"
                        >
                            <Plus size={15} /> New Project
                        </button>
                        <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                            <X size={24} />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    <DataTable
                        data={projects}
                        columns={columns}
                        total={projects.length}
                        page={1}
                        limit={projects.length || 10}
                        onPageChange={() => { }}
                        onEdit={(project) => setEditingProject(project)}
                        loading={loading}
                        customActions={(project: any) => {
                            const rate = project.hourlyRate || 0;
                            let total = 0;

                            if (project.billingMethod === 'Fixed Cost') {
                                total = rate;
                            } else if (rate && project.timesheets && project.timesheets.length > 0) {
                                const totalHours = project.timesheets.reduce((acc: number, t: any) => {
                                    const [h, m] = t.timeSpent.split(':').map(Number);
                                    return acc + (h + m / 60);
                                }, 0);
                                total = totalHours * rate;
                            }

                            const hasInstallments = project.installments && project.installments.length > 0;
                            const paid = hasInstallments ? project.installments.reduce((acc: number, inst: any) => {
                                return inst.status === 'Paid' ? acc + inst.amount : acc;
                            }, 0) : (project.receivedAmount || 0);

                            const remaining = total - paid;

                            if (!hasInstallments) {
                                if (remaining > 0) {
                                    return (
                                        <button
                                            onClick={() => handleCompleteProject(project)}
                                            className="text-green-600 hover:text-green-800 p-1"
                                            title="Complete Project & Pay Remaining"
                                        >
                                            <CheckCircle size={16} />
                                        </button>
                                    );
                                } else if (paid > 0 && remaining <= 0) {
                                    return (
                                        <button
                                            onClick={() => handleUndoComplete(project)}
                                            className="text-orange-500 hover:text-orange-700 p-1"
                                            title="Undo Complete & Delete Payment"
                                        >
                                            <Undo2 size={16} />
                                        </button>
                                    );
                                }
                            }
                            return null;
                        }}
                    />
                </div>

                {editingProject && (
                    <ProjectForm
                        project={editingProject}
                        onClose={() => setEditingProject(null)}
                        onSuccess={handleProjectSuccess}
                    />
                )}

                {creatingProject && (
                    <ProjectForm
                        defaultCustomerId={customerId}
                        onClose={() => setCreatingProject(false)}
                        onSuccess={() => { setCreatingProject(false); handleProjectSuccess(); }}
                    />
                )}

                <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                    <div className="flex justify-between items-center">
                        <div className="flex gap-8">
                            <div>
                                <p className="text-sm text-gray-500">Total Projects Value</p>
                                <p className="text-lg font-bold text-gray-800">{formatCurrency(totalAllProjects, currency)}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Total Paid</p>
                                <p className="text-lg font-bold text-green-600">{formatCurrency(totalPaidAll, currency)}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Total Remaining</p>
                                <p className="text-lg font-bold text-blue-600">{formatCurrency(totalRemainingAll, currency)}</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-100 transition text-gray-700"
                        >
                            Close
                        </button>
                    </div>
                </div>

                <ConfirmationModal
                    isOpen={confirmModalOpen}
                    title="Complete Project"
                    message={`Are you sure you want to mark "${confirmProject?.projectName}" as complete and automatically log a payment of ${formatCurrency(confirmAmount, currency)}?`}
                    confirmText="Yes, Complete Project"
                    cancelText="No, Cancel"
                    onConfirm={onConfirmProject}
                    onCancel={() => {
                        setConfirmModalOpen(false);
                        setConfirmProject(null);
                    }}
                />

                <ConfirmationModal
                    isOpen={undoModalOpen}
                    title="Undo Complete Project"
                    message={`Are you sure you want to undo completion for "${undoProject?.projectName}"? This will reset the project balance and delete the automatically generated payment record.`}
                    confirmText="Yes, Undo Complete"
                    cancelText="No, Keep It"
                    onConfirm={onUndoComplete}
                    onCancel={() => {
                        setUndoModalOpen(false);
                        setUndoProject(null);
                    }}
                />
            </div>
        </div>
    );
};

export default CustomerProjectsModal;
